/** "Review your mistakes" — the learner's practised-but-not-mastered
 * items (worst first), each linking back to the item player. */
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as api from "../../api/exams";
import { ApiError } from "../../api/http";
import { ContentLocked } from "../../components/ContentLocked";
import { Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

export function ReviewPage() {
  const t = useT();
  const { board = "", level = "" } = useParams();
  const [items, setItems] = useState<api.ReviewItem[] | null>(null);
  const [error, setError] = useState(false);
  const [lockedModule, setLockedModule] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .reviewQueue(board, level)
      .then((r) => {
        if (!cancelled) setItems(r.items);
      })
      .catch((err) => {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 403) {
          setLockedModule(err.requiredModule);
        } else {
          setError(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [board, level]);

  if (lockedModule !== null) return <ContentLocked module={lockedModule} />;
  if (error) {
    return (
      <Card>
        <p role="alert" className="text-sm text-danger">{t("common.error.generic")}</p>
      </Card>
    );
  }
  if (!items) {
    return (
      <div className="grid place-items-center py-12">
        <Spinner size={28} />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <div>
        <Link to="/practice" className="text-sm text-muted hover:text-brand">
          ← {t("practice.title")}
        </Link>
        <h1 className="mt-1 text-3xl font-extrabold">{t("practice.readiness.review")}</h1>
        <p className="mt-1 text-muted">{t("practice.review.subtitle")}</p>
      </div>

      {items.length === 0 ? (
        <Card>
          <EmptyState message={t("practice.review.empty")} />
        </Card>
      ) : (
        <ul className="space-y-2">
          {items.map((item) => (
            <li key={item.id}>
              <Link
                to={`/practice/items/${item.id}`}
                className="flex items-center justify-between rounded-control border border-line bg-surface px-4 py-3 transition-all hover:-translate-y-0.5 hover:border-brand"
              >
                <div>
                  <p className="font-semibold text-ink">{item.title}</p>
                  <p className="text-xs text-muted">{item.topic ?? item.part_code}</p>
                </div>
                <span className="text-sm font-semibold text-warning">
                  {t("practice.best")}: {item.best_score}%
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
